import _ from 'lodash';
import { AttendanceType, ClassSessionWithGroupData } from '../../components/addSessions/types';
import {
    CanJoinBeforeInstructor,
    ClassDataUpdate,
    ClassSessionGroupPayload,
    MeetingObj,
    MeetingSettings,
    RequireAuth,
    WaitingRoom
} from './types';

const returnDelta = (object1: any, object2: any) => {
    const delta = {};

    Object.keys(object1).forEach((key) => {
        if (!_.isEqual(object1[key], object2[key])) {
            delta[key] = object2[key];
        }
    });

    return delta;
};

export const returnMeetingDataDelta = (initialData: any, formValues: any) => {
    const initialMeetingSettings = {
        join_before_host: initialData.canJoinBeforeInstructor === CanJoinBeforeInstructor.YES,
        waiting_room: initialData.waitingRoom === WaitingRoom.ON,
        meeting_authentication: initialData.requireAuth === RequireAuth.ON
    };

    const meetingSettings = {
        join_before_host: formValues.canJoinBeforeInstructor === CanJoinBeforeInstructor.YES,
        waiting_room: formValues.waitingRoom === WaitingRoom.ON,
        meeting_authentication: formValues.requireAuth === RequireAuth.ON
    };

    const meetingSettingsDelta: Partial<MeetingSettings> = returnDelta(initialMeetingSettings, meetingSettings);

    const initialMeetingData: Partial<MeetingObj> = {
        topic: initialData.className,
        password: initialData.classPasscode,
        agenda: initialData.description
    };

    const meetingData: Partial<MeetingObj> = {
        topic: formValues.className,
        password: formValues.classPasscode,
        agenda: formValues.description
    };

    const meetingDelta: Partial<MeetingObj> = returnDelta(initialMeetingData, meetingData);

    if (!_.isEmpty(meetingSettingsDelta)) {
        meetingDelta.settings = meetingSettingsDelta;
    }

    return meetingDelta;
};

export const returnClassDataDelta = (initialData: any, formValues: any) => {
    const initialClassData = {
        class_name: initialData.className,
        class_description: initialData.description,
        class_code: initialData.classId,
        content_type: initialData.contentType,
        timezone: initialData.timezone,
        class_times: initialData.class_times,
        class_options: initialData.class_options
    };

    const classData = {
        class_name: formValues.className,
        class_description: formValues.description,
        class_code: formValues.classId,
        content_type: formValues.contentType,
        timezone: formValues.timezone,
        class_times: formValues.class_times,
        class_options: {
            ...initialData.class_options,
            webExperience: formValues.webExperience ?? initialData.class_options.webExperience,
            allowVanillaZoom: formValues.allowVanillaZoom ?? initialData.class_options.allowVanillaZoom,
            unauth_user_access: formValues.unauthorizedUserAccess ?? initialData.class_options.unauth_user_access,
            unauth_user_text: formValues.unauthorizedUserText ?? initialData.class_options.unauth_user_text
        }
    };

    const classDataDelta: Partial<ClassDataUpdate> = returnDelta(initialClassData, classData);

    return classDataDelta;
};

export const groupSessions = (sessions: ClassSessionWithGroupData[]): ClassSessionGroupPayload[] => {
    const classSessionGroupsPayload: { [sessionGroupUuid: string]: ClassSessionGroupPayload } = {};

    sessions.forEach((session: ClassSessionWithGroupData) => {
        if (!session.classSessionGroupUuid) return;
        if (!classSessionGroupsPayload[session.classSessionGroupUuid]) {
            let trackAttendance = false;
            let attendanceLmsPush = false;
            switch (session.attendanceType) {
                case AttendanceType.Native:
                    trackAttendance = true;
                    break;
                case AttendanceType.IntegrateWithLms:
                    trackAttendance = true;
                    attendanceLmsPush = true;
                    break;
                default:
                    break;
            }

            classSessionGroupsPayload[session.classSessionGroupUuid] = {
                class_session_group_uuid: session.classSessionGroupUuid,
                schedule: {
                    class_times: []
                },
                config: {
                    label: session.label,
                    selectedGroups: session.selectedGroups,
                    trackAttendance,
                    attendanceLmsPush
                }
            } as ClassSessionGroupPayload;
        }
        classSessionGroupsPayload[session.classSessionGroupUuid].schedule.class_times.push(
            `${session.start_time}:${session.end_time}`
        );
    });

    return Object.values(classSessionGroupsPayload);
};

export const datesHaveBeenUpdated = (dirtyFields: any) => {
    if (!dirtyFields.dates) {
        return false;
    }

    return dirtyFields.dates.some((date) => {
        if (!_.isEmpty(date)) {
            const keys = Object.keys(date);

            if (keys.length === 1 && keys[0] === 'overnightAcknowledged') {
                return false;
            }
            return true;
        }
        return false;
    });
};
